import { ArrowRotateLeft, CircleExclamation } from "@gravity-ui/icons";
import {
  Alert,
  Button,
  Flex,
  Icon,
  SegmentedRadioGroup,
  Select,
  Text,
  TextInput,
} from "@gravity-ui/uikit";
import { useAction, useAtom } from "@reatom/npm-react";
import { CounterType } from "definitions/counter-api/index.ts";
import {
  ExportFormats,
  UpdateDataExportTaskRequestDto,
} from "definitions/exporter";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useCheckRights } from "src/hooks/useCheckRights.ts";
import { useTranslate } from "src/i18n";
import { getCategoriesFetch } from "src/pages/ProductPage/widgets/ProductFilters/store/fetcher/getCategoriesFetch.ts";
import { getCountProductSampleRequest } from "src/pages/ProductPage/widgets/ProductSamples/store/fetchers/getCountProductSampleRequest.ts";
import { RoutesPaths } from "src/routes/RoutesPaths.ts";
import { productSelectionStore } from "src/store/atoms";
import { getActiveLimiterRequest } from "src/store/fetchers/getLimitersRequest.ts";
import {
  deepEqual,
  generateRandomExportTaskName,
  omitUndefinedDeep,
  pluralNumber,
  toastHandler,
} from "src/util";
import { ShowConfirmDialog } from "src/widgets/Alert/Alert.tsx";

import {
  getExportJobsRequest,
  getExportOptionsRequest,
  getExportTaskOptionsRequest,
  updateExportTasksRequest,
} from "../../store/fetchers";
import { Section, SectionBadge, StickyActionBar } from "./components";
import {
  COLLAPSE_VARIANT_OPTIONS,
  platforms,
} from "./ExportTasksForm.constants.ts";
import css from "./ExportTasksForm.module.scss";
import { ExportTaskFormValues } from "./ExportTasksForm.types.ts";
import { normalizeAvitoFormValues } from "./ExportTasksForm.utils.ts";
import { useExportTasksForm } from "./useExportTasksForm.ts";
import {
  AvitoOptionsForm,
  CronForm,
  MappingForm,
  PriceOptionsForm,
  ProductSampleForm,
  TemplatesForm,
  TransformersForm,
} from "./widgets";
import { migrateRounding } from "./widgets/RoundingOptionForm/migrateRounding.ts";
import { roundingStepRubles } from "./widgets/RoundingOptionForm/RoundingOptionForm.constants.ts";

interface ExportTasksFormProps {
  taskId: string;
}

export const ExportTasksForm = ({ taskId }: ExportTasksFormProps) => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const [isViewCronForm, setIsViewCronForm] = useState(false);

  const [exportOptions] = useAtom(getExportOptionsRequest.dataAtom);
  const [categories] = useAtom(getCategoriesFetch.dataAtom);
  const getExportOptions = useAction(getExportOptionsRequest);
  const loadCategories = useAction(getCategoriesFetch);
  const getExportJobs = useAction(getExportJobsRequest);
  const updateExportTask = useAction(updateExportTasksRequest);
  const getCountProductSample = useAction(getCountProductSampleRequest);
  const { createExportTask, isLoading } = useExportTasksForm();

  const [isExportTaskOptionsLoading] = useAtom(
    getExportTaskOptionsRequest.isLoadingAtom,
  );

  const [activeLimiters] = useAtom(getActiveLimiterRequest.dataAtom);

  const isAccessToStartExport = useCheckRights("START_EXPORT");

  const loading = isLoading || isExportTaskOptionsLoading;

  const [defaultStateOptions, setDefaultStateOptions] =
    useState<ExportTaskFormValues>();

  const getExportTaskOptions = useAction(getExportTaskOptionsRequest);
  const setSelectedSpuIds = useAction(
    productSelectionStore.setSelectedIdsAction,
  );

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useFormContext<ExportTaskFormValues>();

  const selectProductSampleId = watch("selectProductSampleId");
  const priceOptions = watch("priceOptions");
  const withoutPrices = watch("withoutPrices");
  const roundingOption = watch("roundingOption");
  const selectedFormat = watch("format");
  const avitoOptions = watch("avitoOptions");
  const mappingParams = watch("mapping.params");
  const titleTemplate = watch("titleTemplate");
  const descriptionTemplate = watch("descriptionTemplate");

  const isAvitoFormat = selectedFormat === ExportFormats.Avito;

  // Заполненность Avito-параметров для бейджа/раскрытия секций. Шаблон и
  // состояние подставляет бэк (auto-режим) — оператору остаётся указать общие
  // теги фида: category (автозаполняется) и обязательный adType.
  const isAvitoConfigured = !!avitoOptions?.adType && !!avitoOptions?.category;

  const currentFormatInfo = useMemo(
    () => exportOptions.formatsInfo?.find((f) => f.format === selectedFormat),
    [exportOptions.formatsInfo, selectedFormat],
  );

  const supportedCategoryIds = currentFormatInfo?.supportedCategoryIds;
  const hasCategoryRestriction =
    Array.isArray(supportedCategoryIds) && supportedCategoryIds.length > 0;

  // Загружаем дерево категорий, только если у формата реально есть ограничение
  // по categoryId — иначе незачем дёргать API ради информационного алёрта.
  useEffect(() => {
    if (hasCategoryRestriction && !categories) loadCategories();
  }, [hasCategoryRestriction, categories, loadCategories]);

  const categoryNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const c of categories ?? []) {
      if (c.id !== undefined) map.set(c.id, c.name);
    }
    return map;
  }, [categories]);

  const supportedCategoriesLabel = useMemo(() => {
    if (!hasCategoryRestriction) return "";
    return supportedCategoryIds
      .map((id) => {
        const name = categoryNameById.get(id);
        return name ? `${name} (${id})` : String(id);
      })
      .join(", ");
  }, [hasCategoryRestriction, supportedCategoryIds, categoryNameById]);

  const hasAvitoErrors = !!errors.avitoOptions;
  const hasMappingErrors = !!errors.mapping;
  const hasSampleErrors = !!errors.selectProductSampleId;
  const hasPriceErrors = !!errors.priceOptions;

  const formState = watch();

  // Нормализуем default один раз — он меняется только на load/save таски.
  // watch() возвращает новый объект на каждый рендер, поэтому мемо для current
  // всё равно пересчитывается, но default-снапшот не таскаем через рекурсию
  // каждый keystroke. omitUndefinedDeep — см. JSDoc о причинах нормализации.
  const normalizedDefault = useMemo(
    () =>
      defaultStateOptions
        ? omitUndefinedDeep(normalizeAvitoFormValues(defaultStateOptions))
        : undefined,
    [defaultStateOptions],
  );

  const isDirty = useMemo(() => {
    if (!normalizedDefault) return true;
    return !deepEqual(
      normalizedDefault,
      omitUndefinedDeep(normalizeAvitoFormValues(formState)),
    );
  }, [normalizedDefault, formState]);

  useEffect(() => {
    if (taskId) {
      const controller = new AbortController();

      getExportTaskOptions({
        id: taskId,
      })
        ?.then((data) => {
          if (controller.signal.aborted) return;

          const { cronTask, language, ...options } = data;

          if (cronTask) {
            setIsViewCronForm(true);
          }

          // Миграция ДО reset И снапшота default — одним объектом, иначе
          // старая задача с ROUNDING_PRICE открылась бы «грязной».
          const formValues = migrateRounding({
            ...options,
            cronTask,
            language,
          });
          reset(formValues);
          setDefaultStateOptions(formValues);
        })
        .catch((error) => {
          if (error.name !== "AbortError") {
            console.error("Ошибка при получении опций задачи экспорта:", error);
          }
        });

      return () => {
        controller.abort();
      };
    } else {
      reset();
    }
  }, [getExportTaskOptions, reset, setSelectedSpuIds, taskId]);

  useEffect(() => {
    getExportOptions();
  }, [getExportOptions]);

  const formats: { label: string; value: ExportFormats }[] = useMemo(
    () =>
      exportOptions.formats.map((format) => ({
        label: format,
        value: format,
      })),
    [exportOptions.formats],
  );

  const platformOptions = useMemo(
    () =>
      platforms.map((platform) => ({
        value: platform.value,
        content: (
          <Flex alignItems="center" gap={2}>
            <Icon data={platform.Icon} size={16} />
            {platform.content}
          </Flex>
        ),
        title: platform.title,
      })),
    [],
  );

  const priceRulesCount = priceOptions?.length ?? 0;

  const priceSectionBadge = useMemo<SectionBadge | undefined>(() => {
    if (withoutPrices) return { text: "Без цен", theme: "info" };
    const parts: string[] = [];
    if (priceRulesCount > 0) {
      parts.push(
        `${priceRulesCount} ${pluralNumber(priceRulesCount, ["правило", "правила", "правил"])}`,
      );
    }
    if (roundingOption) {
      parts.push(`округление до ${roundingStepRubles(roundingOption.zeros)} ₽`);
    }
    return parts.length
      ? { text: parts.join(" · "), theme: "info" }
      : undefined;
  }, [withoutPrices, priceRulesCount, roundingOption]);

  const mappingBadge = useMemo(() => {
    if (hasMappingErrors) return { text: "Ошибка", theme: "danger" as const };
    if (!mappingParams) return undefined;
    const count = Object.values(mappingParams).filter(
      (entry) => Array.isArray(entry?.paramKeys) && entry.paramKeys.length > 0,
    ).length;
    return count > 0
      ? {
          text: `${count} ${pluralNumber(count, ["поле", "поля", "полей"])}`,
          theme: "info" as const,
        }
      : undefined;
  }, [hasMappingErrors, mappingParams]);

  const templatesBadge = useMemo(() => {
    const enabledCount =
      (titleTemplate?.template ? 1 : 0) +
      (descriptionTemplate?.template ? 1 : 0);
    if (enabledCount === 0) return undefined;
    const text =
      enabledCount === 2
        ? "Title + Description"
        : titleTemplate?.template
          ? "Title"
          : "Description";
    return { text, theme: "info" as const };
  }, [titleTemplate?.template, descriptionTemplate?.template]);

  const onSubmit = handleSubmit(async (rawData) => {
    const data = normalizeAvitoFormValues(rawData);
    const cronTask = isViewCronForm ? data.cronTask : undefined;

    if (!data.selectProductSampleId) {
      toastHandler.error({
        name: "noProductSampleError",
        title: "Не выбрана выборка",
        content: "Выберите выборку товаров",
      });
      return;
    }

    const activeLimiter = activeLimiters?.find(
      (limiter) => limiter.type === CounterType.PoizonRuRequests,
    );

    if (activeLimiter?.total) {
      const productSampleCount = await getCountProductSample(
        data.selectProductSampleId,
      );

      const needRequests = productSampleCount.data.count;

      const freeRequests =
        activeLimiter.total - (activeLimiter?.spent || 0) || 0;

      if (freeRequests < needRequests)
        return toastHandler.error({
          name: "lowRequestCountError",
          title: "Не хватает запросов",
          content: `У вашего тарифа не хватает запросов, чтобы выполнить задачу`,
        });

      return ShowConfirmDialog(
        <Alert
          theme="info"
          title={`При запуске задачи у вас спишется ${needRequests} ${pluralNumber(needRequests, ["запрос", "запроса", "запросов"])}`}
          message="Вы действительно хотите запустить?"
        />,
        "Подтвердите действие",
      ).then(async (result) => {
        if (result) {
          await createExportTask({
            ...data,
            id: taskId,
            cronTask,
          });
          await getExportJobs({ id: taskId });
        }
      });
    }

    await createExportTask({
      ...data,
      id: taskId,
      cronTask,
    });
    await getExportJobs({ id: taskId });
  });

  const onUpdateTask = handleSubmit((rawData) => {
    const data = normalizeAvitoFormValues(rawData);
    const cronTask = isViewCronForm ? data.cronTask : undefined;

    const payload: UpdateDataExportTaskRequestDto = {
      ...data,
      id: taskId,
      cronTask,
    };
    updateExportTask(payload);

    setDefaultStateOptions({
      ...data,
      cronTask,
    });
  });

  const onUndoTask = useCallback(() => {
    reset(defaultStateOptions);
  }, [defaultStateOptions, reset]);

  return (
    <form className={css.exportTasksForm} onSubmit={onSubmit}>
      <div className={css.scrollArea}>
        <div className={css.identityBlock}>
          <div className={css.field}>
            <Text variant="body-1" className={css.fieldLabel}>
              Формат выгрузки
            </Text>
            <Controller
              name="format"
              rules={{
                required: "Выберите формат выгрузки",
              }}
              control={control}
              render={({ field, fieldState }) => (
                <Select
                  placeholder="Выберите формат"
                  {...field}
                  value={field.value ? [field.value] : []}
                  onUpdate={(value) => field.onChange(value[0])}
                  validationState={
                    fieldState.error === undefined ? undefined : "invalid"
                  }
                  errorMessage={fieldState.error?.message}
                  width="max"
                  size="l"
                >
                  {formats.map((format) => (
                    <Select.Option key={format.value} value={format.value}>
                      {format.label}
                    </Select.Option>
                  ))}
                </Select>
              )}
            />
          </div>

          <div className={css.field}>
            <Text variant="body-1" className={css.fieldLabel}>
              Платформа
            </Text>
            <Controller
              name="platform"
              rules={{
                required: "Выберите платформу",
              }}
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <SegmentedRadioGroup
                    options={platformOptions}
                    value={field.value}
                    onChange={(event) => field.onChange(event.target.value)}
                    size="l"
                    width="max"
                  />
                  {fieldState.error && (
                    <div className={css.fieldError}>
                      {fieldState.error.message}
                    </div>
                  )}
                </>
              )}
            />
          </div>

          {hasCategoryRestriction && (
            <Alert
              theme="info"
              view="filled"
              title={`Формат ${selectedFormat} работает для ограниченного набора категорий`}
              message={`Поддерживаемые категории: ${supportedCategoriesLabel}. Товары из других категорий будут пропущены при экспорте.`}
            />
          )}

          <div className={css.field}>
            <Text variant="body-1" className={css.fieldLabel}>
              Название файла
            </Text>
            <Controller
              name="filename"
              rules={{
                required: "Укажите название файла",
              }}
              control={control}
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  value={field.value || ""}
                  onChange={(e) => field.onChange(e.target.value)}
                  validationState={
                    fieldState.error === undefined ? undefined : "invalid"
                  }
                  errorMessage={fieldState.error?.message}
                  placeholder="Например, winter-export"
                  size="l"
                  endContent={
                    <Button
                      onClick={() => {
                        field.onChange(generateRandomExportTaskName());
                      }}
                      view={"flat"}
                      title="Сгенерировать имя"
                    >
                      <Icon data={ArrowRotateLeft} size={18}></Icon>
                    </Button>
                  }
                />
              )}
            />
          </div>

          <div className={css.field}>
            <Text variant="body-1" className={css.fieldLabel}>
              Свёртка вариантов
            </Text>
            <Controller
              name="collapseVariantsByProductId"
              control={control}
              render={({ field }) => (
                <Select
                  placeholder="Без свёртки (каждый вариант отдельно)"
                  {...field}
                  value={field.value ? [field.value] : []}
                  onUpdate={(value) => field.onChange(value[0])}
                  hasClear
                  width="max"
                  size="l"
                >
                  {COLLAPSE_VARIANT_OPTIONS.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              )}
            />
          </div>
        </div>

        <Section
          title="Выборка товаров"
          defaultExpanded
          forceExpanded={hasSampleErrors}
          badge={
            selectProductSampleId
              ? undefined
              : { text: "Не выбрано", theme: "warning" }
          }
        >
          <ProductSampleForm />
          {!selectProductSampleId && (
            <Flex gap={2} alignItems="center" className={css.sampleHint}>
              <Icon data={CircleExclamation} size={16} />
              <Text variant="body-1" color="secondary">
                Без выборки задачу нельзя запустить
              </Text>
            </Flex>
          )}
        </Section>

        {isAvitoFormat && (
          <Section
            title="Параметры Avito"
            defaultExpanded
            forceExpanded={hasAvitoErrors}
            badge={
              hasAvitoErrors
                ? { text: "Ошибка", theme: "danger" }
                : isAvitoConfigured
                  ? undefined
                  : { text: "Не заполнено", theme: "warning" }
            }
          >
            <AvitoOptionsForm />
          </Section>
        )}

        {isAvitoFormat && (
          <Section
            title="Маппинг полей Avito"
            defaultExpanded={isAvitoConfigured}
            forceExpanded={hasMappingErrors}
            badge={mappingBadge}
          >
            <MappingForm />
          </Section>
        )}

        <TransformersForm />

        <Section
          title="Шаблоны Title / Description"
          defaultExpanded={
            !!titleTemplate?.template || !!descriptionTemplate?.template
          }
          badge={templatesBadge}
        >
          <TemplatesForm />
        </Section>

        <Section
          title="Ценовые правила"
          defaultExpanded={
            withoutPrices || priceRulesCount > 0 || !!roundingOption
          }
          forceExpanded={hasPriceErrors}
          badge={priceSectionBadge}
        >
          <PriceOptionsForm />
        </Section>

        <Section
          title="Расписание"
          defaultExpanded={isViewCronForm}
          badge={
            isViewCronForm ? { text: "Включено", theme: "info" } : undefined
          }
        >
          <CronForm isView={isViewCronForm} setIsView={setIsViewCronForm} />
        </Section>
      </div>

      <StickyActionBar
        hint={isDirty ? "Есть несохранённые изменения" : undefined}
      >
        {isDirty ? (
          <>
            <Button
              view="action"
              size="l"
              onClick={onUpdateTask}
              loading={loading}
            >
              Сохранить
            </Button>
            <Button view="outlined" size="l" onClick={onUndoTask}>
              Отменить
            </Button>
          </>
        ) : isAccessToStartExport ? (
          <Button
            view="action"
            size="l"
            type="submit"
            disabled={!selectProductSampleId}
            loading={loading}
          >
            Запустить задачу
          </Button>
        ) : (
          <Flex direction="column" gap={2} width="100%">
            <Alert
              theme="warning"
              title="Ваша подписка закончилась. Чтобы запустить выгрузку — обновите тариф."
            />
            <Button
              view="outlined-warning"
              size="l"
              width="max"
              onClick={() => navigate(RoutesPaths.LK.SUBSCRIBE)}
            >
              {t("buy_subscribe", "Купить подписку")}
            </Button>
          </Flex>
        )}
      </StickyActionBar>
    </form>
  );
};
